import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { DocumentInterruptSource, Idle } from '@ng-idle/core';
import { Store } from '@ngxs/store';
import {
  AppConfigState,
  AuthService,
  ConfigResourceService,
  ConfirmationModalComponent,
  DrugsService,
  LoginService,
  PageTags,
  SiteService,
  SiteType
} from 'vnext-shared';

const SESSION_IDLE_MINUTE_RESOURCE = 'Spa_Site_Session_Idle_Minutes';
const SESSION_IDLE_MINUTE_DEFAULT = 15;
const SESSION_TIMEOUT_MINUTE_RESOURCE = 'Spa_Site_Session_Timeout_Minutes';
const SESSION_TIMEOUT_MINUTE_DEFAULT = 5;
/**
 * Contains session idle modal functionality including base configuration
 */
@Injectable({ providedIn: 'root' })
export class SessionIdleService {
  idleModal: NgbModalRef;

  // making this private so people don't use SessionIdleService to grab siteType
  private siteType: string;

  constructor(
    private modalService: NgbModal,
    private configResourceService: ConfigResourceService,
    private loginService: LoginService,
    private authService: AuthService,
    private store: Store,
    private route: Router,
    private idle: Idle,
    private drugsService: DrugsService,
    private siteService: SiteService,
    private ngZone: NgZone
  ) {}

  initializeIdle(): void {
    this.siteType = this.configResourceService.getProcessedResourceValue('Spa_Site_SiteType', null, SiteType.CONSUMER);
    this.configureIdle();
    this.idle.watch();
  }

  configureIdle(): void {
    const isDebugMode: boolean = this.configResourceService.getProcessedResourceValue(
      'Spa_Site_Session_IdleTimeout_EnableDebugMode',
      null,
      false
    );
    const idleSeconds: number =
      this.configResourceService.getProcessedResourceValue(SESSION_IDLE_MINUTE_RESOURCE, null, SESSION_IDLE_MINUTE_DEFAULT) * 60;
    this.idle.setIdle(idleSeconds);
    const timeoutSeconds: number =
      this.configResourceService.getProcessedResourceValue(SESSION_TIMEOUT_MINUTE_RESOURCE, null, SESSION_TIMEOUT_MINUTE_DEFAULT) * 60;
    this.idle.setTimeout(timeoutSeconds);
    this.idle.setInterrupts([
      new DocumentInterruptSource('mousemove keydown DOMMouseScroll mousewheel mousedown touchstart touchmove scroll')
    ]);

    this.idle.onTimeout.subscribe(async () => {
      // If API session expired, refresh the JWT in order to save drug list and create a member session
      // It also covers #SPA-3923
      if (!this.authService.isAuthenticatedForApiCalls()) {
        await this.authService.loadJWT();
      }
      // Debuggin SPA-6905, to be removed
      if (isDebugMode) {
        console.log('[NgIdle] onTimeout', new Date());
      }

      this.drugsService.saveDrugList();
      // running through the logout regardless of logged in state as it is the most
      // comprehensive way to clear the store right now and redirect to correct page
      if (this.isCurrentRouteNotAgentLogin()) {
        // On Chrome, after 5 minutes of inactivity (another browser's tab selected or another window active)
        // causes the navigation to be triggered outside Angular zone
        this.ngZone.run(() => {
          this.loginService.logout(this.siteType);
        });
      }
      if (this.idleModal) {
        this.idleModal.close();
      }
      this.modalService.dismissAll();
      this.idle.watch();
    });
    this.idle.onIdleStart.subscribe(() => {
      // Debuggin SPA-6905, to be removed
      if (isDebugMode) {
        console.log('[NgIdle] onIdleStart', new Date());
      }
      // Hide session expiry popup on the Agent login page.
      if (this.isCurrentRouteNotAgentLogin()) {
        this.openSessionIdleModal();
      }
    });
    if (isDebugMode) {
      // Debuggin SPA-6905, to be removed
      this.idle.onInterrupt.subscribe(eventArgs => console.log('[NgIdle] onInterrupt', { eventArgs, timestamp: new Date() }));
      this.idle.onTimeoutWarning.subscribe(countdown => console.log(`[NgIdle] onTimeoutWarning - ${countdown} seconds`));
      this.idle.onIdleEnd.subscribe(() => console.log('[NgIdle] onIdleEnd', new Date()));
    }
  }

  openSessionIdleModal(): void {
    if (this.idleModal) {
      this.idleModal.close();
    }
    const options: NgbModalOptions = {
      ariaLabelledBy: 'modal_header_title',
      centered: true
    };
    this.idleModal = this.modalService.open(ConfirmationModalComponent, options);
    this.idleModal.componentInstance.title = this.configResourceService.getProcessedResourceValue(
      'Spa_Site_Idle_Modal_Title',
      null,
      'Session timeout'
    );
    this.idleModal.componentInstance.prompt = this.configResourceService.getProcessedResourceValue(
      'Spa_Site_Idle_Modal_Prompt',
      null,
      'In order to protect your privacy, we will end your session automatically due to inactivity. Otherwise, you may choose to ' +
        'continue your session.'
    );
    this.idleModal.componentInstance.cancelButton = this.configResourceService.getProcessedResourceValue(
      'Spa_Site_Idle_End_Text',
      null,
      'End now'
    );
    this.idleModal.componentInstance.confirmButton = this.configResourceService.getProcessedResourceValue(
      'Spa_Site_Idle_Continue_Text',
      null,
      'Continue session'
    );
    this.idleModal.componentInstance.cancelAction = async () => {
      // If API session expired, refresh the JWT in order to save drug list and create a member session
      if (!this.authService.isAuthenticatedForApiCalls()) {
        await this.authService.loadJWT();
      }
      this.drugsService.saveDrugList();
      this.loginService.logout(this.siteType);
    };
    this.idleModal.componentInstance.confirmAction = () => {
      this.idle.watch();
    };
  }

  private isCurrentRouteNotAgentLogin(): boolean {
    const currentRouteUrl = this.route.url;
    const pageUrl: string = this.store.selectSnapshot(AppConfigState.hrefByTag(PageTags.AGENT_LOGIN));
    const newLocal = !currentRouteUrl.includes(pageUrl);
    return newLocal;
  }
}
